import { useCallback, useEffect, useRef, useState } from 'react'
import { ArrowLeft, BookOpen, Link2, PanelRight, Plus, X } from 'lucide-react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { PLANT_HREF } from '@atbash-labs/plantview-links'
import {
  setSidebarFocus,
  useAppDispatch,
  type ConversationSummary
} from '@axilon-apps/domains-ontology-ui-state'
import { type AppContextEntry, useChatAppContext } from '../../chat-app-context'
import type { ChatPrefillState } from '../../components/Notifications/useNotificationActions'

import { AgentMemoryEditor } from '../../components/Chat/AgentMemoryEditor'
import {
  CHAT_SHARE_ALLOWED_ACCESS_LEVELS,
  CHAT_SHARE_SCOPE_DESCRIPTION
} from '../../components/Chat/chat-behavior'
import { CHAT_PAGE_HEADER_MEASURE } from '../../components/Chat/chat-measure'
import {
  consumeChatReturnPath,
  consumeMemoryReturnPath,
  rememberMemoryReturnPath,
  requestRailExpandOnNextMount
} from '../../components/Chat/chat-handoff'
import { CHAT_MEMORY_PATH, isChatMemoryPath } from '../../components/Chat/chat-route'
import {
  currentIngestState,
  currentIngestTitle,
  type CurrentIngestDescriptor
} from '../../components/Chat/CurrentIngestEntry'
import { OrchestratorDeleteDialog, OrchestratorPane } from '../../components/Chat/OrchestratorPane'
import { ReadOnlyConversationBadge } from '../../components/Chat/ReadOnlyConversationBadge'
import { useAgentIngestSession } from '../../components/Chat/useAgentIngestSession'
import { useIngestSessionAttention } from '../../components/Chat/useIngestSessionAttention'
import { useOffBranchUnstructuredNeedsInput } from '../../components/Chat/useOffBranchUnstructuredNeedsInput'
import { useOrchestratorChatController } from '../../components/Chat/useOrchestratorChatController'
import { ingestFullSessionPath } from '../../components/IngestSession/ingest-session-route'
import { RequestAccessPanel, ResourceShareButton } from '../../components/ResourceSharingDialog'
import { Button } from '../../components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '../../components/ui/tooltip'
import { getOperatorView, isCoreView } from '../../lib/operator-view-flag'
import { cn } from '../../lib/utils'
import { ChatSidePanel } from './ChatSidePanel'

function dockFallbackPath(): string {
  return isCoreView(getOperatorView()) ? '/dashboards' : PLANT_HREF.root
}

const ChatPage = () => {
  const { conversationId } = useParams<{ conversationId?: string }>()
  const location = useLocation()
  const { pathname } = location
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const isMemory = isChatMemoryPath(pathname)
  const [deniedConversationId, setDeniedConversationId] = useState<string | null>(null)
  const handleConversationCopied = useCallback(
    (id: string): void => navigate(`/chat/${id}`, { replace: true }),
    [navigate]
  )
  // `null` is the new-conversation landing; omit on memory so a thread opened
  // from `/chat/:id` is still there when the user hits Back.
  const chat = useOrchestratorChatController({
    initialActiveTabId: isMemory ? undefined : (conversationId ?? null),
    onConversationCopied: handleConversationCopied
  })
  const { session, isActive, hasFailed } = useAgentIngestSession()
  const offBranchNeedsInput = useOffBranchUnstructuredNeedsInput({
    skip: Boolean(session)
  })
  // Observe only. The rail owns the one-shot auto-focus, so consuming it here
  // would swallow the signal for whichever page the user visits next.
  const { pendingQuestionId } = useIngestSessionAttention(session, {
    requestAttention: false
  })
  const ingestHandoff = session ?? offBranchNeedsInput.session
  const ingestQuestionId = pendingQuestionId ?? offBranchNeedsInput.questionId

  useEffect(() => {
    void chat.loadConversations()
  }, [chat.loadConversations])

  // URL → store for post-mount conversation changes. Hydration honors
  // `initialActiveTabId` on first mount so a persisted tab cannot clobber
  // a pasted `/chat/:id`. An id the API does not know is looked up for
  // request-access; a true miss degrades to landing.
  useEffect(() => {
    if (conversationId === undefined) {
      setDeniedConversationId(null)
      return
    }
    if (conversationId === chat.activeTabId) {
      setDeniedConversationId(null)
      return
    }
    let cancelled = false
    void chat.handleSelectFromHistory(conversationId).then((ok) => {
      if (cancelled) return
      if (ok) {
        setDeniedConversationId(null)
        return
      }
      setDeniedConversationId(conversationId)
    })
    return () => {
      cancelled = true
    }
    // Only the route id should retrigger this. Handler identities change as
    // tabs load and would re-issue the same select.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, navigate])

  // `/chat` is the new-conversation landing. Drop a rail-focused or
  // persisted thread so the greeting is what you get from the nav, `/`,
  // and post-login — not the last conversation you had open.
  useEffect(() => {
    if (isMemory) return
    if (conversationId !== undefined) return
    if (chat.activeTabId === null) return
    chat.handleNewTab()
    // Only the route should retrigger this. Running after submit would wipe
    // the conversation the store→URL effect is about to address.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, isMemory])

  // A notification's Discuss action lands here with a prefilled draft and the
  // subject pinned as app context. Seed once, then drop the router state so a
  // refresh or Back does not re-seed. Nothing is sent until the user submits.
  const [pinnedContext, setPinnedContext] = useState<AppContextEntry[] | null>(null)
  useEffect(() => {
    const prefill = (location.state as Partial<ChatPrefillState> | null)?.chatPrefill
    if (!prefill || conversationId !== undefined) return
    chat.setDraft(prefill.draft)
    setPinnedContext(prefill.appContext)
    navigate(pathname, { replace: true, state: null })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state])
  useEffect(() => {
    if (chat.activeTabId !== null) setPinnedContext(null)
  }, [chat.activeTabId])
  useChatAppContext(pinnedContext ?? undefined)
  const composerHeader =
    pinnedContext && pinnedContext.length > 0 ? (
      <div
        className="mb-1 flex flex-wrap items-center gap-1.5 px-1 pt-0.5"
        aria-label="Pinned context"
      >
        <span className="inline-flex items-center gap-1 text-11 text-ink-3">
          <Link2 className="size-3" aria-hidden="true" />
          From notification
        </span>
        {pinnedContext.map((entry) => (
          <span
            key={entry.key}
            className="inline-flex max-w-full items-center gap-1 rounded-md bg-st-info-bg px-1.5 py-0.5 text-11"
          >
            <span className="text-ink-3">{entry.key}</span>
            <span className="max-w-72 truncate font-medium text-ink">{entry.value}</span>
          </span>
        ))}
        <button
          type="button"
          aria-label="Remove pinned context"
          className="ml-auto inline-flex size-5 cursor-pointer items-center justify-center rounded text-ink-3 hover:bg-accent"
          onClick={() => setPinnedContext(null)}
        >
          <X className="size-3" />
        </button>
      </div>
    ) : null

  const previousActiveTabIdRef = useRef(chat.activeTabId)

  // Store → URL when a new conversation is created on the landing (first
  // submit). A thread that was already focused must not be written into
  // `/chat` — that is what used to bounce the landing onto the last chat.
  // `/chat/memory` owns its URL and must not be overwritten.
  useEffect(() => {
    const previousActiveTabId = previousActiveTabIdRef.current
    previousActiveTabIdRef.current = chat.activeTabId
    if (isMemory) return
    if (conversationId !== undefined) return
    if (chat.activeTabId === null) return
    if (previousActiveTabId !== null) return
    navigate(`/chat/${chat.activeTabId}`, { replace: true })
  }, [chat.activeTabId, conversationId, isMemory, navigate])

  const handleSelect = useCallback(
    (id: string): void => {
      void chat.handleSelectFromHistory(id)
      if (id !== conversationId) navigate(`/chat/${id}`)
    },
    [chat, conversationId, navigate]
  )

  const handleNewTab = useCallback((): void => {
    chat.handleNewTab()
    if (isMemory || conversationId !== undefined) navigate('/chat')
  }, [chat, conversationId, isMemory, navigate])

  const handleOpenMemory = useCallback((): void => {
    rememberMemoryReturnPath(pathname)
    navigate(CHAT_MEMORY_PATH)
  }, [navigate, pathname])

  const handleBackFromMemory = useCallback((): void => {
    navigate(consumeMemoryReturnPath() ?? '/chat')
  }, [navigate])

  const handleDockToSide = useCallback((): void => {
    requestRailExpandOnNextMount()
    navigate(consumeChatReturnPath() ?? dockFallbackPath())
  }, [navigate])

  // Same handoff as the pane's Open full session. Leaving `/chat` remounts the
  // rail, so ask it to come back on the session tab instead of the 48 px strip.
  const handleOpenCurrentIngest = useCallback((): void => {
    if (!ingestHandoff) return
    requestRailExpandOnNextMount()
    dispatch(setSidebarFocus({ kind: 'session', id: ingestHandoff.id }))
    navigate(ingestFullSessionPath(ingestHandoff.id, { questionId: ingestQuestionId }), {
      state: { focusCurrentIngest: true }
    })
  }, [dispatch, ingestHandoff, ingestQuestionId, navigate])

  const currentIngest: CurrentIngestDescriptor | undefined = ingestHandoff
    ? {
        title: currentIngestTitle(ingestHandoff.runName, ingestHandoff.fileName),
        state: session
          ? currentIngestState({
              pendingQuestion: Boolean(pendingQuestionId),
              failed: hasFailed,
              active: isActive
            })
          : 'question',
        onSelect: handleOpenCurrentIngest,
        ...(session ? {} : { hint: 'Switch to ingest branch' })
      }
    : undefined

  const isLanding = !isMemory && chat.activeTabId === null && deniedConversationId === null
  const showingRequestAccess =
    !isMemory && deniedConversationId !== null && deniedConversationId === conversationId
  const activeConversation = chat.conversations.find(
    (conversation: ConversationSummary) => conversation.id === chat.activeTabId
  )
  const title = showingRequestAccess ? 'Request access' : (activeConversation?.title ?? 'Chat')

  const newChatButton = (
    <Button type="button" size="sm" onClick={handleNewTab}>
      <Plus className="size-4" aria-hidden="true" />
      New chat
    </Button>
  )

  const pageControls = (
    <TooltipProvider delayDuration={700}>
      {activeConversation?.accessScopeId && !showingRequestAccess ? (
        <ResourceShareButton
          resourceName={activeConversation.title}
          resourceLabel="conversation"
          accessScopeId={activeConversation.accessScopeId}
          allowedAccessLevels={CHAT_SHARE_ALLOWED_ACCESS_LEVELS}
          scopeDescription={CHAT_SHARE_SCOPE_DESCRIPTION}
          toolbar
        />
      ) : null}
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label="Agent memory"
            onClick={handleOpenMemory}
          >
            <BookOpen className="size-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom">Agent memory</TooltipContent>
      </Tooltip>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label="Dock to side"
            onClick={handleDockToSide}
          >
            <PanelRight className="size-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom">Dock to side</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden [contain:size]">
      <div className="flex min-h-0 flex-1">
        <div className="relative flex min-h-0 min-w-0 flex-1 flex-col">
          {isMemory ? (
            <>
              <header className="mx-auto w-full max-w-344 shrink-0 px-6 pt-0.5 pb-2 sm:px-8 lg:px-12">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="-ml-2 mb-0.5 h-7 px-2 text-xs text-muted-foreground"
                  onClick={handleBackFromMemory}
                >
                  <ArrowLeft className="size-3.5" />
                  Back
                </Button>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h1 className="m-0 text-xl font-semibold tracking-tight text-foreground">
                      Agent memory
                    </h1>
                    <p className="mt-0.5 mb-0 text-13 text-muted-foreground">
                      Reference every agent loads before it answers — your organization’s shared
                      conventions, plus your own preferences.
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    {newChatButton}
                    <TooltipProvider delayDuration={700}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon-sm"
                            aria-label="Dock to side"
                            onClick={handleDockToSide}
                          >
                            <PanelRight className="size-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom">Dock to side</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </div>
              </header>
              <div className="flex min-h-0 flex-1 flex-col">
                <AgentMemoryEditor />
              </div>
            </>
          ) : (
            <>
              <header
                data-testid="chat-page-header"
                className={cn(
                  CHAT_PAGE_HEADER_MEASURE,
                  'flex min-h-10 items-center justify-between gap-3 py-1 transition-colors duration-300 motion-reduce:transition-none',
                  isLanding
                    ? 'absolute top-0 left-1/2 z-10 -translate-x-1/2 border-transparent'
                    : 'shrink-0 border-b border-border pb-4'
                )}
              >
                <div className="min-w-0 flex-1">
                  {isLanding ? null : (
                    <div className="flex min-w-0 items-center gap-2">
                      <h1 className="m-0 min-w-0 truncate text-17 font-semibold tracking-tight text-foreground">
                        {title}
                      </h1>
                      {activeConversation && !chat.canContribute ? (
                        <ReadOnlyConversationBadge />
                      ) : null}
                    </div>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {isLanding ? null : newChatButton}
                  <div className="flex items-center gap-0.5">{pageControls}</div>
                </div>
              </header>
              <div className="flex min-h-0 flex-1 flex-col">
                {showingRequestAccess && conversationId ? (
                  <RequestAccessPanel
                    resourceType="conversation"
                    id={conversationId}
                    onAccessGranted={() => {
                      void chat.loadConversations().then(() => {
                        void chat.handleSelectFromHistory(conversationId).then((ok) => {
                          if (ok) setDeniedConversationId(null)
                        })
                      })
                    }}
                    onNotFound={() => {
                      setDeniedConversationId(null)
                      chat.handleNewTab()
                      navigate('/chat', { replace: true })
                    }}
                    notFoundMessage="Conversation not found."
                  />
                ) : (
                  <OrchestratorPane chat={chat} layout="page" composerHeader={composerHeader} />
                )}
              </div>
            </>
          )}
        </div>
        <ChatSidePanel
          chat={chat}
          view="conversations"
          onSelect={handleSelect}
          {...(currentIngest ? { currentIngest } : {})}
        />
      </div>
      <OrchestratorDeleteDialog chat={chat} />
    </div>
  )
}

export default ChatPage
